<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/ops/dashboard">Ops</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb">Debtors Finance</span>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/ops/df/invoices">Invoices</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Invoice</span>
    </nav>
  </header>

  @if (isLoading()) {
    <mat-card appearance="outlined" aria-label="Loading invoice">
      <mat-card-content class="skeleton-card">
        <span class="sg-skeleton sg-skeleton--heading" style="width: 45%"></span>
        <span class="sg-skeleton sg-skeleton--text" style="width: 30%"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span>
      </mat-card-content>
    </mat-card>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--bordered sg-empty--error">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this invoice</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <div class="error-actions">
        <button mat-stroked-button type="button" (click)="load()">
          <app-icon name="refresh" [size]="18" /> Try again
        </button>
        <a mat-stroked-button routerLink="/ops/df/invoices">Back to queue</a>
      </div>
    </div>
  } @else if (invoice(); as inv) {
    @if (serverError(); as error) {
      <div class="error-banner" role="alert">{{ error }}</div>
    }

    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Invoice {{ inv.invoice_number }}</h1>
        <p class="page-head__subtitle">{{ inv.invoice_amount | zar }} · {{ inv.debtor_name }}</p>
      </div>
      <div class="page-head__actions">
        <app-status-chip [status]="inv.invoice_status" />
      </div>
    </div>

    <!-- AI Advisory -->
    @if (inv.latest_ai_recommendation) {
      <mat-card appearance="outlined" class="ai-card">
        <mat-card-content>
          <h2 class="section-title">AI Advisory</h2>
          <dl class="facts">
            <div class="facts__row">
              <dt>Recommendation</dt>
              <dd>{{ aiRecLabel(inv.latest_ai_recommendation) }}</dd>
            </div>
          </dl>
          <p class="muted">
            Advisory only — the analyst's verification and decision are recorded below.
          </p>
        </mat-card-content>
      </mat-card>
    }

    <!-- Invoice details -->
    <mat-card appearance="outlined">
      <mat-card-content>
        <h2 class="section-title">Invoice details</h2>
        <dl class="facts">
          @if (inv.apparent_overdue_flag) {
            <div class="facts__row" data-testid="apparent-overdue">
              <dt>Appears overdue</dt>
              <dd>Check the terms before verifying. The client says: {{ inv.apparent_overdue_reason }}</dd>
            </div>
          }
          <div class="facts__row">
            <dt>Invoice number</dt>
            <dd>{{ inv.invoice_number }}</dd>
          </div>
          <div class="facts__row">
            <dt>Amount</dt>
            <dd>{{ inv.invoice_amount | zar }}</dd>
          </div>
          <div class="facts__row">
            <dt>Advance rate</dt>
            <dd>{{ inv.advance_rate !== null ? inv.advance_rate + '%' : (null | dash) }}</dd>
          </div>
          <div class="facts__row">
            <dt>Funded amount</dt>
            <dd>{{ inv.funded_amount | zar }}</dd>
          </div>
          <div class="facts__row">
            <dt>Invoice date</dt>
            <dd>{{ inv.invoice_date ? (inv.invoice_date | date: 'mediumDate') : '—' }}</dd>
          </div>
          <div class="facts__row">
            <dt>Due date</dt>
            <dd>{{ inv.due_date ? (inv.due_date | date: 'mediumDate') : '—' }}</dd>
          </div>
          <div class="facts__row">
            <dt>Funded at</dt>
            <dd>{{ inv.funded_at ? (inv.funded_at | date: 'medium') : '—' }}</dd>
          </div>
          <div class="facts__row">
            <dt>Settled at</dt>
            <dd>{{ inv.settled_at ? (inv.settled_at | date: 'medium') : '—' }}</dd>
          </div>
        </dl>
      </mat-card-content>
    </mat-card>

    <!-- Documents -->
    @if (inv.documents.length > 0) {
      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Documents</h2>
          <ul class="doc-list">
            @for (doc of inv.documents; track doc.short_id) {
              <li class="doc-list__item">
                <app-icon name="document" [size]="16" />
                <span>{{ doc.file_name }}</span>
              </li>
            }
          </ul>
        </mat-card-content>
      </mat-card>
    }

    <!-- Status timeline -->
    @if (inv.status_events.length > 0) {
      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Status history</h2>
          <ol class="timeline">
            @for (event of inv.status_events; track $index) {
              <li class="timeline__item">
                <app-status-chip [status]="event.to_status" />
                <span class="timeline__meta">
                  {{ event.created_at | date: 'medium' }}
                  @if (event.actor_name) {
                    · {{ event.actor_name }}
                  }
                </span>
                @if (event.note) {
                  <p class="timeline__note">{{ event.note }}</p>
                }
              </li>
            }
          </ol>
        </mat-card-content>
      </mat-card>
    }

    <!-- Actions -->
    <mat-card appearance="outlined" class="actions-card">
      <mat-card-content>
        <h2 class="section-title">Actions</h2>

        <div class="action-buttons">
          <!-- Verify -->
          @if (inv.invoice_status === 'SUBMITTED') {
            <button
              mat-stroked-button
              type="button"
              (click)="verify()"
              [disabled]="isActioning()"
              data-testid="verify-btn"
            >
              Verify
            </button>
          }

          <!-- Fund -->
          @if (inv.invoice_status === 'APPROVED_FOR_FUNDING') {
            <details class="action-panel">
              <summary class="action-panel__title">Fund</summary>
              @if (needsCcWarning()) {
                <div class="warning-banner" role="alert">
                  Above 80% of the invoice a Credit Committee reference (cc_ref) is required, and
                  the advance cannot exceed what is authorised for this debtor.
                </div>
              }
              <form [formGroup]="fundForm" (ngSubmit)="fund()" class="form">
                <mat-form-field appearance="outline">
                  <mat-label>Funded amount (ZAR)</mat-label>
                  <input matInput type="number" formControlName="funded_amount" min="1" />
                  @if (
                    fundForm.controls.funded_amount.touched &&
                    fundForm.controls.funded_amount.invalid
                  ) {
                    <mat-error>Required</mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>CC reference (required above 80%)</mat-label>
                  <input matInput formControlName="cc_ref" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Note (optional)</mat-label>
                  <textarea matInput formControlName="note" rows="2"></textarea>
                </mat-form-field>
                <div class="form__actions">
                  <button
                    mat-flat-button
                    color="primary"
                    type="submit"
                    [disabled]="isActioning()"
                    data-testid="fund-btn"
                  >
                    @if (isActioning()) {
                      <mat-spinner diameter="20" />
                    } @else {
                      <span>Fund</span>
                    }
                  </button>
                </div>
              </form>
            </details>
          }

          <!-- Settle -->
          @if (inv.invoice_status === 'FUNDED') {
            <button
              mat-stroked-button
              type="button"
              (click)="settle()"
              [disabled]="isActioning()"
              data-testid="settle-btn"
            >
              Settle
            </button>
          }

          <!-- Reject -->
          @if (inv.invoice_status === 'SUBMITTED' || inv.invoice_status === 'APPROVED_FOR_FUNDING') {
            <button
              mat-stroked-button
              type="button"
              class="btn-destructive"
              (click)="reject()"
              [disabled]="isActioning()"
              data-testid="reject-btn"
            >
              Reject
            </button>
          }
        </div>
      </mat-card-content>
    </mat-card>
  }
</div>
